import React, { useState, useMemo } from 'react';
import Badge from './Badge';
import Toggle from './Toggle';
import Select from './Select';

const ALL_DECISIONS = ['approve', 'edit', 'reject'];

const DECISION_COLORS = {
    approve: 'text-emerald-600 border-emerald-300 bg-emerald-50 accent-emerald-600',
    edit: 'text-blue-600 border-blue-300 bg-blue-50 accent-blue-600',
    reject: 'text-red-600 border-red-300 bg-red-50 accent-red-600',
};

function groupToolsByPrefix(tools) {
    const groups = {};
    for (const name of tools) {
        const parts = name.split('_');
        let prefix = 'Other';
        if (parts.length >= 2) {
            const known = [
                'watchlist', 'portfolio', 'nebula', 'note',
                'screener', 'membership', 'credit', 'alert',
                'get', 'add', 'update', 'delete', 'create',
                'search', 'mcp',
            ];
            const first = parts[0].toLowerCase();
            if (known.includes(first) && parts.length >= 3) {
                prefix = parts[1].charAt(0).toUpperCase() + parts[1].slice(1);
            } else {
                prefix = first.charAt(0).toUpperCase() + first.slice(1);
            }
        }
        if (!groups[prefix]) groups[prefix] = [];
        groups[prefix].push(name);
    }
    return Object.entries(groups).sort(([a], [b]) => a.localeCompare(b));
}

function DecisionChips({ decisions = [], onChange }) {
    return (
        <div className="flex items-center gap-1">
            {ALL_DECISIONS.map(d => {
                const active = decisions.includes(d);
                return (
                    <button
                        key={d}
                        type="button"
                        onClick={() => {
                            const next = active
                                ? decisions.filter(x => x !== d)
                                : [...decisions, d];
                            onChange(next.length > 0 ? next : [d]);
                        }}
                        className={`px-1.5 py-0.5 rounded text-[10px] font-medium border transition-colors ${
                            active
                                ? DECISION_COLORS[d]
                                : 'text-gray-400 border-gray-200 bg-white hover:bg-gray-50'
                        }`}
                    >
                        {d.charAt(0).toUpperCase() + d.slice(1)}
                    </button>
                );
            })}
        </div>
    );
}

function ToolRow({ name, config, onToggle, onDecisionsChange }) {
    const checked = !!config;
    const decisions = config?.allowed_decisions || ALL_DECISIONS;

    return (
        <div className="flex items-center gap-2 py-1 px-2 rounded hover:bg-gray-50 group">
            <input
                type="checkbox"
                checked={checked}
                onChange={() => onToggle(name, !checked)}
                className="h-3.5 w-3.5 rounded border-gray-300 text-gray-900 focus:ring-gray-500 focus:ring-offset-0 shrink-0"
            />
            <span className="text-xs font-mono text-gray-700 flex-1 truncate min-w-0">{name}</span>
            {checked && (
                <DecisionChips
                    decisions={decisions}
                    onChange={(d) => onDecisionsChange(name, d)}
                />
            )}
        </div>
    );
}

function ToolGroup({ title, tools, interruptOn, onToggle, onDecisionsChange, onToggleAll }) {
    const allChecked = tools.every(t => !!interruptOn[t]);
    const someChecked = tools.some(t => !!interruptOn[t]);

    return (
        <div className="mb-2 last:mb-0">
            <div className="flex items-center gap-2 py-1 px-2">
                <input
                    type="checkbox"
                    checked={allChecked}
                    ref={(el) => { if (el) el.indeterminate = someChecked && !allChecked; }}
                    onChange={() => onToggleAll(tools, !allChecked)}
                    className="h-3.5 w-3.5 rounded border-gray-300 text-gray-900 focus:ring-gray-500 focus:ring-offset-0 cursor-pointer"
                />
                <span className="text-xs font-medium text-gray-600 uppercase tracking-wider cursor-pointer" onClick={() => onToggleAll(tools, !allChecked)}>{title}</span>
                <Badge variant="outline">{tools.filter(t => !!interruptOn[t]).length}/{tools.length}</Badge>
            </div>
            <div className="ml-5 border-l border-gray-100 pl-2">
                {tools.map(tool => (
                    <ToolRow
                        key={tool}
                        name={tool}
                        config={interruptOn[tool]}
                        onToggle={onToggle}
                        onDecisionsChange={onDecisionsChange}
                    />
                ))}
            </div>
        </div>
    );
}

function MCPServerGroup({ server, interruptOn, onToggle, onDecisionsChange, onToggleAll }) {
    const tools = server.tools || [];
    if (tools.length === 0) return null;

    const toolNames = tools.map(t => t.name || t.value || (typeof t === 'string' ? t : '')).filter(Boolean);
    const allChecked = toolNames.every(t => !!interruptOn[t]);
    const someChecked = toolNames.some(t => !!interruptOn[t]);

    return (
        <div className="mb-2 last:mb-0">
            <div className="flex items-center gap-2 py-1 px-2">
                <input
                    type="checkbox"
                    checked={allChecked}
                    ref={(el) => { if (el) el.indeterminate = someChecked && !allChecked; }}
                    onChange={() => onToggleAll(toolNames, !allChecked)}
                    className="h-3.5 w-3.5 rounded border-gray-300 text-gray-900 focus:ring-gray-500 focus:ring-offset-0 cursor-pointer"
                />
                <span className="text-xs font-medium text-gray-600 cursor-pointer" onClick={() => onToggleAll(toolNames, !allChecked)}>{server.name || server.id}</span>
                <Badge variant="outline">{toolNames.filter(t => !!interruptOn[t]).length}/{toolNames.length}</Badge>
            </div>
            <div className="ml-5 border-l border-gray-100 pl-2">
                {toolNames.map(tool => (
                    <ToolRow
                        key={tool}
                        name={tool}
                        config={interruptOn[tool]}
                        onToggle={onToggle}
                        onDecisionsChange={onDecisionsChange}
                    />
                ))}
            </div>
        </div>
    );
}

export default function InterruptConfig({
    enabled = true,
    mode = 'default',
    interruptOn = {},
    defaultInterruptOn = {},
    availableTools = [],
    mcpServers = [],
    onEnabledChange,
    onModeChange,
    onInterruptOnChange,
}) {
    const [search, setSearch] = useState('');

    const defaultToolNames = useMemo(
        () => Object.keys(defaultInterruptOn),
        [defaultInterruptOn],
    );

    const builtinToolNames = useMemo(
        () => availableTools.map(t => t.name || t.value || (typeof t === 'string' ? t : '')).filter(Boolean),
        [availableTools],
    );

    const allToolGroups = useMemo(() => {
        const names = new Set([...builtinToolNames, ...defaultToolNames]);
        return groupToolsByPrefix([...names].sort());
    }, [builtinToolNames, defaultToolNames]);

    const filteredGroups = useMemo(() => {
        if (!search) return allToolGroups;
        const q = search.toLowerCase();
        return allToolGroups
            .map(([group, tools]) => [group, tools.filter(t => t.toLowerCase().includes(q))])
            .filter(([, tools]) => tools.length > 0);
    }, [allToolGroups, search]);

    const filteredMCP = useMemo(() => {
        if (!search) return mcpServers;
        const q = search.toLowerCase();
        return mcpServers
            .map(s => ({
                ...s,
                tools: (s.tools || []).filter(t => {
                    const n = t.name || t.value || (typeof t === 'string' ? t : '');
                    return n.toLowerCase().includes(q);
                }),
            }))
            .filter(s => s.tools.length > 0);
    }, [mcpServers, search]);

    const totalSelected = Object.keys(interruptOn).length;
    const totalDefault = defaultToolNames.length;

    const handleToggle = (name, checked) => {
        const next = { ...interruptOn };
        if (checked) {
            next[name] = defaultInterruptOn[name] || { allowed_decisions: [...ALL_DECISIONS] };
        } else {
            delete next[name];
        }
        onInterruptOnChange(next);
    };

    const handleDecisionsChange = (name, decisions) => {
        const next = { ...interruptOn };
        next[name] = { ...next[name], allowed_decisions: decisions };
        onInterruptOnChange(next);
    };

    const handleToggleAll = (tools, checked) => {
        const next = { ...interruptOn };
        for (const t of tools) {
            if (checked) {
                next[t] = defaultInterruptOn[t] || { allowed_decisions: [...ALL_DECISIONS] };
            } else {
                delete next[t];
            }
        }
        onInterruptOnChange(next);
    };

    const handleSelectAllBuiltin = () => {
        const allNames = allToolGroups.flatMap(([, tools]) => tools);
        const allChecked = allNames.every(t => !!interruptOn[t]);
        handleToggleAll(allNames, !allChecked);
    };

    const handleResetToDefault = () => {
        onInterruptOnChange({ ...defaultInterruptOn });
    };

    return (
        <div className="space-y-3">
            <Toggle
                label="Enable HITL (Human-in-the-loop)"
                checked={enabled}
                onChange={onEnabledChange}
            />

            {enabled && (
                <>
                    <div className="flex items-center gap-3">
                        <Select
                            label="Mode"
                            options={[
                                { value: 'default', label: `Default (${totalDefault} tools)` },
                                { value: 'custom', label: 'Custom' },
                            ]}
                            value={mode}
                            onChange={(e) => onModeChange(e.target.value)}
                            className="w-52"
                        />
                        {mode === 'custom' && (
                            <Badge variant={totalSelected > 0 ? 'info' : 'warning'}>
                                {totalSelected} tool{totalSelected !== 1 ? 's' : ''} selected
                            </Badge>
                        )}
                    </div>

                    {mode === 'custom' && (
                        <div className="space-y-2">
                            <div className="flex items-center gap-2">
                                <input
                                    type="text"
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    placeholder="Filter tools..."
                                    className="flex-1 h-7 px-2 text-xs border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-gray-400"
                                />
                                <button
                                    type="button"
                                    onClick={handleResetToDefault}
                                    className="text-xs text-blue-600 hover:text-blue-700 whitespace-nowrap"
                                >
                                    Reset to default
                                </button>
                            </div>

                            <div className="flex items-center gap-3 px-2 py-1.5 bg-gray-50 rounded-md border border-gray-100">
                                <span className="text-[10px] text-gray-500 uppercase tracking-wider">Legend:</span>
                                {ALL_DECISIONS.map(d => (
                                    <span key={d} className={`px-1.5 py-0.5 rounded text-[10px] font-medium border ${DECISION_COLORS[d]}`}>
                                        {d.charAt(0).toUpperCase() + d.slice(1)}
                                    </span>
                                ))}
                            </div>

                            {filteredGroups.length > 0 && (
                                <div className="border border-gray-200 rounded-lg overflow-hidden">
                                    <div className="flex items-center justify-between px-3 py-2 bg-gray-50 border-b border-gray-200">
                                        <span className="text-xs font-medium text-gray-700">Built-in Tools</span>
                                        <button
                                            type="button"
                                            onClick={handleSelectAllBuiltin}
                                            className="text-[11px] text-gray-500 hover:text-gray-700"
                                        >
                                            Toggle all
                                        </button>
                                    </div>
                                    <div className="max-h-72 overflow-y-auto p-2">
                                        {filteredGroups.map(([group, tools]) => (
                                            <ToolGroup
                                                key={group}
                                                title={group}
                                                tools={tools}
                                                interruptOn={interruptOn}
                                                onToggle={handleToggle}
                                                onDecisionsChange={handleDecisionsChange}
                                                onToggleAll={handleToggleAll}
                                            />
                                        ))}
                                    </div>
                                </div>
                            )}

                            {filteredMCP.length > 0 && (
                                <div className="border border-gray-200 rounded-lg overflow-hidden">
                                    <div className="px-3 py-2 bg-gray-50 border-b border-gray-200">
                                        <span className="text-xs font-medium text-gray-700">MCP Tools</span>
                                    </div>
                                    <div className="max-h-72 overflow-y-auto p-2">
                                        {filteredMCP.map(server => (
                                            <MCPServerGroup
                                                key={server.id}
                                                server={server}
                                                interruptOn={interruptOn}
                                                onToggle={handleToggle}
                                                onDecisionsChange={handleDecisionsChange}
                                                onToggleAll={handleToggleAll}
                                            />
                                        ))}
                                    </div>
                                </div>
                            )}

                            {filteredGroups.length === 0 && filteredMCP.length === 0 && (
                                <p className="text-xs text-gray-400 text-center py-4">No tools match filter</p>
                            )}
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
